import {
  Box,
  Button,
  Collapse,
  CollapsibleIconButton,
  IconButton,
  SimpleSelect,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@chronosphereio/chrono-ui';
import { Stack } from '@mui/system';
import { castDraft, Draft, produce } from 'immer';
import { WritableDraft } from 'immer/dist/internal.js';
import { DeleteOutline as DeleteIcon, Plus as PlusIcon } from 'mdi-material-ui';
import React, { ChangeEvent, ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChartsTheme } from '../../../components/context/ChartsProvider';
import { GenericThresholdOptions, StepOptions, ThresholdColorPalette, ThresholdOptions } from '../../../core';
import { OptionsEditorControl, OptionsEditorGroup } from '../OptionsEditorLayout';
import { ThresholdColorPicker } from './ThresholdColorPicker';

export interface ThresholdsEditorProps<K extends StepOptions, T extends GenericThresholdOptions<K>> {
  onChange: (thresholds: T) => void;
  thresholds?: T;
  hideDefault?: boolean;
  hideThresholdMode?: boolean;
  renderStepOptions: (
    props: Pick<ThresholdStepInputProps<WithKey<K> | Omit<K, 'value'>>, 'step' | 'onStepChange'>
  ) => ReactNode;
  renderColorPicker?: (props: {
    step: WithKey<K> | Omit<K, 'value'>;
    onStepChange: (newStep: WithKey<K> | Omit<K, 'value'>) => void;
    defaultColor: string;
  }) => ReactNode;
  defaultPalette?: ThresholdColorPalette;
  isValueMapping?: boolean;
}

export const DEFAULT_STEP = 10;

// So because all of the steps are just in an array and there is no unique identifier for them
// and they can be reordered based on values changing, we need to add some unique key to each one
// to keep proper ordering

// The downside to this is that we will keep the state with these keys local to this component so if there
// were changes to the steps external to this, they will not be reflected
export type WithKey<T> = T & { key: string };
type WithKeyedSteps<K extends StepOptions, T extends GenericThresholdOptions<K>> = Omit<T, 'steps'> & {
  steps: Array<WithKey<K>>;
};

export function ThresholdsEditor<K extends StepOptions, T extends GenericThresholdOptions<K>>(
  props: ThresholdsEditorProps<K, T>
) {
  const [steps, setSteps] = useState(
    props.thresholds?.steps?.map<WithKey<K>>((step, i) => ({ ...step, key: `key-${i}` }))
  );

  const wrappedOnChange = useCallback(
    (thresholds: WithKeyedSteps<K, T>) => {
      // remove the key and pass to onchange
      const keylessSteps = thresholds.steps?.map<K>((stepWithKey) => {
        const { key: _key, ...step } = stepWithKey;

        return step as unknown as K;
      });

      // Some TS hacking here
      // I'm taking the keyed type and need to make it a non keyed type
      // I can't figure out how to do that via immer so I'm just casting it here and then
      // doing the mutations in the produce function
      const keylessThresholds = thresholds as unknown as T;
      const newThresholds = produce(keylessThresholds, (draft) => {
        draft.steps = castDraft(keylessSteps);
      });

      props.onChange(newThresholds);
      setSteps(thresholds.steps);
    },
    [setSteps, props]
  );

  const castThresholds = props.thresholds as unknown as WithKeyedSteps<K, T>;

  const keyedThresholds =
    props.thresholds !== undefined
      ? produce(castThresholds, (draft: WritableDraft<WithKeyedSteps<K, T>>) => {
          if (steps !== undefined) {
            draft.steps = steps as unknown as Draft<WithKeyedSteps<K, T>['steps']>;
          }
        })
      : undefined;

  const emptyThresholds = {} as WithKeyedSteps<K, T>;

  return (
    <ThresholdsEditorGeneric {...props} thresholds={keyedThresholds || emptyThresholds} onChange={wrappedOnChange} />
  );
}

// This is not the public interface, but a cleaned up inner interface
//type InnerEditorProps<K extends StepOptions, T extends WithKeyedSteps<K, GenericThresholdOptions<K>>> =
export function ThresholdsEditorGeneric<
  K extends StepOptions,
  T extends WithKeyedSteps<K, GenericThresholdOptions<K>>,
>({
  thresholds,
  onChange: baseOnChange,
  hideDefault,
  hideThresholdMode,
  renderStepOptions,
  renderColorPicker,
  defaultPalette,
  isValueMapping,
}: ThresholdsEditorProps<K, T> & { thresholds: T }) {
  const chartsTheme = useChartsTheme();

  const palette = defaultPalette?.palette || chartsTheme.thresholds.palette;

  const defaultColor = thresholds?.default_color || defaultPalette?.defaultColor || chartsTheme.thresholds.defaultColor;

  const steps = thresholds?.steps;
  const showThresholdMode = !hideThresholdMode;

  // Anytime we call on change, we want to make sure the steps get sorted into the correct order
  const onChange = useCallback(
    (updatedThresholds: T) => {
      baseOnChange(
        produce(updatedThresholds, (draft) => {
          if (draft.steps !== undefined && draft.steps.length > 0) {
            draft.steps.sort((a, b) => a.value - b.value);
          }
        })
      );
    },
    [baseOnChange]
  );

  // every time a new threshold is added, we want to focus the recently added input
  const recentlyAddedInputRef = useRef<HTMLInputElement | null>(null);
  const focusRef = useRef(false);
  useEffect(() => {
    if (!recentlyAddedInputRef.current || !focusRef.current) return;
    recentlyAddedInputRef.current?.focus();
    focusRef.current = false;
  }, [steps?.length]);

  const handleStepChange = (newStep: WithKey<K>, i: number) => {
    onChange(
      produce(thresholds, (draft) => {
        if (thresholds !== undefined) {
          const step = draft?.steps?.[i];
          if (step) {
            draft.steps[i] = castDraft(newStep);
          }
        }
      })
    );
  };

  const handleBaseChange = (newBase: T['base']) => {
    onChange(
      produce(thresholds, (draft) => {
        if (draft !== undefined) {
          draft.base = castDraft(newBase);
        }
      })
    );
  };

  const deleteThreshold = (i: number): void => {
    if (thresholds !== undefined) {
      const updatedThresholds = produce(thresholds, (draft) => {
        if (draft.steps) {
          draft.steps.splice(i, 1);
        }
      });
      onChange(updatedThresholds);
    }
  };

  const addThresholdInput = (): void => {
    focusRef.current = true;
    if (thresholds === undefined) {
      onChange({
        steps: [{ value: DEFAULT_STEP }],
      } as T);
    } else if (
      (thresholds && thresholds.steps === undefined) ||
      (thresholds && thresholds.steps !== undefined && thresholds.steps.length === 0)
    ) {
      onChange(
        produce(thresholds, (draft) => {
          draft.steps = castDraft([{ value: DEFAULT_STEP, key: 'key-0' }] as unknown as Array<WithKey<K>>);
        })
      );
    } else {
      onChange(
        produce(thresholds, (draft) => {
          const steps = draft.steps;
          if (steps?.length > 0) {
            const lastStep = steps[steps.length - 1];
            const color = palette[steps.length] ?? getRandomColor(); // we will assign color from the palette first, then generate random color
            steps.push(
              castDraft({
                color,
                value: (lastStep?.value ?? 0) + DEFAULT_STEP,
                key: `key-${steps.length}`,
              } as unknown as WithKey<K>)
            ); // set new threshold value to last step value + 10
          }
        })
      );
    }
  };

  const handleModeChange = (event: React.MouseEvent, value: string): void => {
    const mode = value === 'Percent' ? 'Percent' : undefined;
    if (thresholds !== undefined) {
      onChange(
        produce(thresholds, (draft) => {
          draft.mode = mode;
        })
      );
    } else {
      onChange({ mode } as T);
    }
  };

  const renderStepColorPicker = useMemo(() => {
    if (renderColorPicker === undefined) {
      return undefined;
    }

    return (props: { step: WithKey<K>; onStepChange: (step: WithKey<K>) => void; defaultColor: string }) => {
      // Need to cast here to get typed properly
      return renderColorPicker(props as unknown as ColorPickerProps<Omit<K, 'value'>>);
    };
  }, [renderColorPicker]);

  return (
    <OptionsEditorGroup
      title={isValueMapping ? 'Value mappings' : 'Thresholds'}
      icon={
        <Tooltip Content={isValueMapping ? 'Add value mapping' : 'Add threshold'}>
          <IconButton
            size="small"
            aria-label={isValueMapping ? 'add value mapping' : 'add threshold'}
            onClick={addThresholdInput}
          >
            <PlusIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      {showThresholdMode && (
        <OptionsEditorControl
          label="Mode"
          description="Percent means thresholds relative to min & max"
          control={
            <ToggleButtonGroup
              value={thresholds?.mode ?? 'Absolute'}
              onChange={handleModeChange}
              sx={{ height: '36px', marginLeft: 'auto' }}
            >
              <ToggleButton aria-label="absolute" value="Absolute" sx={{ fontWeight: 500 }}>
                Absolute
              </ToggleButton>
              <ToggleButton aria-label="percent" value="Percent" sx={{ fontWeight: 500 }}>
                Percent
              </ToggleButton>
            </ToggleButtonGroup>
          }
        />
      )}
      {steps
        ?.map((step, i) => {
          const inputProps: ThresholdStepInputProps<WithKey<K>> = {
            step,
            onStepChange: (step) => handleStepChange(step, i),
            inputRef: i === steps.length - 1 ? recentlyAddedInputRef : undefined,
            color: step.color ?? palette[i] ?? defaultColor,
            value: step.value,
            mode: thresholds?.mode,
            onDelete: () => {
              deleteThreshold(i);
            },
            renderColorPicker: renderStepColorPicker,
            isValueMapping,
          };

          return (
            <ThresholdStepInput key={step.key || i} {...inputProps}>
              {renderStepOptions({ step, onStepChange: (step) => handleStepChange(step as WithKey<K>, i) })}
            </ThresholdStepInput>
          );
        })
        .reverse()}
      {!hideDefault && (
        <ThresholdBaseInput
          base={thresholds.base || ({} as Omit<K, 'value'>)}
          onBaseChange={handleBaseChange}
          key={'base-step'}
          color={thresholds?.base?.color ?? defaultColor}
          value={thresholds?.steps?.[0]?.value} // This should be the value of the first step
          mode={thresholds?.mode}
          renderColorPicker={renderColorPicker}
          isValueMapping={isValueMapping}
        >
          {renderStepOptions({ step: thresholds.base || ({} as K), onStepChange: handleBaseChange })}
        </ThresholdBaseInput>
      )}
    </OptionsEditorGroup>
  );
}

// https://www.paulirish.com/2009/random-hex-color-code-snippets/
export const getRandomColor = () => {
  return (
    '#' +
    Math.floor(Math.random() * 16777216)
      .toString(16)
      .padStart(6, '0')
  );
};

export interface ThresholdStepInputProps<T extends Omit<StepOptions, 'value'>> {
  step: T;
  color: string; // We pass in this color to use if a color is not on the step
  value: number;
  onDelete: () => void;
  inputRef?: RefObject<HTMLInputElement | null>;
  mode?: ThresholdOptions['mode'];
  onStepChange: (newStep: T) => void;
  renderColorPicker?: (props: ColorPickerProps<T>) => ReactNode;
  isValueMapping?: boolean;
}

export interface ColorPickerProps<T extends Omit<StepOptions, 'value'>> {
  step: T;
  onStepChange: (newStep: T) => void;
  defaultColor: string;
}

export function ThresholdBaseInput<T extends Omit<StepOptions, 'value'>>({
  base,
  color,
  value,
  mode,
  onBaseChange,
  renderColorPicker,
  children,
  isValueMapping,
}: {
  base: T;
  color: string;
  value: number | undefined;
  mode?: ThresholdOptions['mode'];
  onBaseChange: (newBase: T) => void;
  renderColorPicker?: (props: ColorPickerProps<T>) => ReactNode;
  isValueMapping?: boolean;
} & { children: ReactNode }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = useCallback(() => setIsExpanded((current) => !current), [setIsExpanded]);

  const handleColorChange = useCallback(
    (color: string) => {
      onBaseChange(
        produce(base, (draft) => {
          draft.color = color;
        })
      );
    },
    [onBaseChange, base]
  );
  return (
    <div>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box flexGrow={0}>
          <CollapsibleIconButton onClick={toggleExpanded} collapsed={!isExpanded} />
        </Box>

        {isValueMapping ? (
          <Box flexGrow={1}>
            <Typography noWrap>Default</Typography>
          </Box>
        ) : (
          <Box flexGrow={1}>
            {value !== undefined ? (
              <Typography noWrap>
                When value is &lt; {value}
                {mode === 'Percent' ? '%' : ''}
              </Typography>
            ) : (
              <Typography noWrap>Base settings</Typography>
            )}
          </Box>
        )}

        <Box flexGrow={0}>
          {renderColorPicker ? (
            renderColorPicker({ step: base, onStepChange: onBaseChange, defaultColor: color })
          ) : (
            <ThresholdColorPicker label={'base-color'} color={color} onColorChange={handleColorChange} />
          )}
        </Box>
      </Stack>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box pt={1} pb={1} pl={3}>
          {children}
        </Box>
      </Collapse>
    </div>
  );
}

const VALUE_MATCHER_OPTIONS = [
  { label: '>=', key: 'GreaterThanOrEqual' },
  { label: '=', key: 'Equal' },
];

const DEFAULT_VALUE_MATCHER_KEY = 'GreaterThanOrEqual';

export function ThresholdStepInput<T extends WithKey<StepOptions>>({
  step,
  inputRef,
  mode,
  onDelete,
  children,
  color,
  onStepChange,
  renderColorPicker,
  isValueMapping,
}: ThresholdStepInputProps<T> & { children: ReactNode }) {
  const { value } = step;
  // Handle 0, null, and undefined - show empty string so placeholder is visible
  const [localValue, setLocalValue] = useState(!value ? '' : value.toString());
  const handleValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setLocalValue(event.currentTarget.value);
    },
    [setLocalValue]
  );

  const handleBlur = useCallback(() => {
    onStepChange(
      produce(step, (draft) => {
        draft.value = Number(localValue);
      })
    );
  }, [onStepChange, localValue, step]);

  const handleColorChange = useCallback(
    (color: string) => {
      onStepChange(
        produce(step, (draft) => {
          draft.color = color;
        })
      );
    },
    [onStepChange, step]
  );

  const handleOperatorChange = useCallback(
    (operator: 'GreaterThanOrEqual' | 'Equal') => {
      onStepChange(
        produce(step, (draft) => {
          if (operator === 'Equal') {
            draft.operator = operator;
          } else {
            // Implicitly defaults to `GreaterThanOrEqual` since this is an optional field:
            delete draft.operator;
          }
        })
      );
    },
    [onStepChange, step]
  );

  const [key, setKey] = useState(0); // use key to cause input to lose focus when pressing enter
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = useCallback(() => setIsExpanded((current) => !current), [setIsExpanded]);

  return (
    <div>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box flexGrow={0}>
          <CollapsibleIconButton ariaLabelSubject="threshold" onClick={toggleExpanded} collapsed={!isExpanded} />
        </Box>

        <Box flexGrow={0}>
          <Typography noWrap>
            When value is{' '}
            {isValueMapping ? (
              <Box data-testid={`valueMappingOperator ${step.key}`} sx={{ marginLeft: 1, display: 'inline-flex' }}>
                <SimpleSelect
                  sx={{ minWidth: '65px' }}
                  onChange={(e) => handleOperatorChange(e.target.value as 'GreaterThanOrEqual' | 'Equal')}
                  options={VALUE_MATCHER_OPTIONS}
                  value={step.operator ?? DEFAULT_VALUE_MATCHER_KEY}
                />
              </Box>
            ) : (
              '>='
            )}
          </Typography>
        </Box>
        <Box flexGrow={1} width={'100%'}>
          <TextField
            fullWidth
            key={key}
            inputRef={inputRef}
            type="number"
            value={localValue}
            placeholder="0"
            onChange={handleValueChange}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleBlur();
                setKey(key + 1);
              }
            }}
            InputProps={{
              endAdornment: mode === 'Percent' ? <Box paddingLeft={1}>%</Box> : undefined,
            }}
          />
        </Box>

        <Box flexGrow={0}>
          {renderColorPicker !== undefined ? (
            renderColorPicker({
              step,
              onStepChange,
              defaultColor: color,
            })
          ) : (
            <ThresholdColorPicker label={step.key} color={color} onColorChange={handleColorChange} />
          )}
        </Box>
      </Stack>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box pt={1} pb={1} pl={3}>
          {children}
        </Box>

        <Stack direction="row" alignItems="flex-end" justifyContent="end">
          <Box flexGrow={0}>
            <Button variant="tertiary" StartIcon={<DeleteIcon />} onClick={onDelete} size="small">
              Delete threshold
            </Button>
          </Box>
        </Stack>
      </Collapse>
    </div>
  );
}
